import { ChevronDown, Check, Shield } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useOrg } from '@/context/OrgContext'
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { OrgLogo } from '@/components/OrgLogo'
import { cn } from '@/lib/utils'

interface Props {
  className?: string
}

// Pill-shaped org switcher: logo + name + chevron inside a single
// rounded-full container. Static pill when there's only one org;
// dropdown trigger when there are multiple or the user is a platform
// admin (admin link appears at the bottom of the menu).
export function OrgSwitcher({ className }: Props) {
  const { memberships, activeOrg, setActiveOrgId, isPlatformAdmin } = useOrg()
  const navigate = useNavigate()
  if (!activeOrg) return null

  const Logo = (
    <OrgLogo
      url={activeOrg.logo_url}
      name={activeOrg.nombre}
      variant={activeOrg.logo_variant}
      className="w-7 h-7 rounded-full text-[10px]"
    />
  )

  const hasSwitcher = memberships.length > 1 || isPlatformAdmin

  if (!hasSwitcher) {
    return (
      <div className={cn('org-pill', className)}>
        {Logo}
        <span className="op-name">{activeOrg.nombre}</span>
      </div>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className={cn('org-pill', className)}>
          {Logo}
          <span className="op-name">{activeOrg.nombre}</span>
          <ChevronDown className="op-chev w-3.5 h-3.5" strokeWidth={2.5} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64 max-h-[70vh] overflow-y-auto">
        <DropdownMenuLabel className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground/70 font-medium px-3 py-2">
          Switch organization
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {memberships.map(m => {
          const selected = m.org_id === activeOrg.id
          return (
            <DropdownMenuItem
              key={m.org_id}
              onClick={() => setActiveOrgId(m.org_id)}
              className="cursor-pointer"
            >
              <div className="w-4 shrink-0">
                {selected && <Check className="w-3.5 h-3.5 text-primary" />}
              </div>
              <OrgLogo
                url={m.organization.logo_url}
                name={m.organization.nombre}
                variant={m.organization.logo_variant}
                className="w-6 h-6 rounded-md text-[10px]"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{m.organization.nombre}</p>
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">
                  {roleLabel(m.rol_nivel)}
                </p>
              </div>
            </DropdownMenuItem>
          )
        })}

        {isPlatformAdmin && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => navigate('/admin/orgs')}
              className="cursor-pointer"
            >
              <div className="w-4 shrink-0" />
              <div className="w-6 h-6 rounded-md bg-muted flex items-center justify-center shrink-0">
                <Shield className="w-3.5 h-3.5 text-muted-foreground" />
              </div>
              <p className="text-sm font-medium">Platform admin</p>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function roleLabel(rol: 'A' | 'B' | 'C') {
  if (rol === 'A') return 'Manager'
  if (rol === 'B') return 'Artist'
  return 'Crew'
}
